import {
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    Output,
    EventEmitter,
    inject,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { AuthService, ConfigService, SearchService, SettingsService } from '@gallaghercp/core-frontend';
import { NavigationStart, Router } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { TranslatePipe } from '@gallaghercp/core-frontend-contracts';
import { IconComponent } from '@gallaghercp/core-components/icon';
import { ButtonComponent } from '@gallaghercp/core-components/button';

@Component({
    selector: 'core-search-v2',
    imports: [FormsModule, ReactiveFormsModule, TranslatePipe, IconComponent, ButtonComponent],
    templateUrl: './search-v2.component.html',
    styleUrl: './search-v2.component.scss',
})
export class SearchV2Component implements OnInit, OnDestroy {
    protected config = inject(ConfigService);

    @Input() autoFocus?: boolean = false;
    @Input() customIcon?: string;
    @Input() customCloseIcon?: string = this.config.get('search')?.customCloseIcon;
    @Input() closeButtonSize?: string = this.config.get('search')?.closeButtonSize;
    @Output() searchSubmitted = new EventEmitter<void>();

    @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
    @ViewChild('searchForm', { read: ElementRef }) searchForm?: ElementRef<HTMLFormElement>;
    @ViewChild('mobileContainer') mobileContainer?: ElementRef<HTMLElement>;

    queryField: FormControl = new FormControl(this.search.query$.getValue());
    subscription: Subscription = new Subscription();
    showClearButton?: boolean;
    isSearchOpen?: boolean = false;
    isDarkModeEnabled: boolean = this.config.isEnabled('isDarkModeEnabled', false);
    reflectToInnerIcon: boolean = this.config.get('search')?.reflectToInnerIcon ?? true;

    getCloseButtonSize(): string | null {
        if (!this.closeButtonSize) {
            return null;
        }
        const size = this.closeButtonSize.trim();
        return /^\d+$/.test(size) ? `${size}px` : size;
    }

    constructor(
        public search: SearchService,
        public settings: SettingsService,
        protected auth: AuthService,
        protected router: Router,
        protected breakpoint: BreakpointObserver,
    ) {}

    ngOnInit() {
        this.subscription.add(
            this.queryField.valueChanges.subscribe(term => {
                if (term && term.length > 0) {
                    this.showClearButton = true;
                }
                if (term == '' || term == null) {
                    this.showClearButton = false;
                }
            }),
        );

        this.subscription.add(
            this.queryField.valueChanges.pipe(debounceTime(800), distinctUntilChanged()).subscribe(term => {
                if (term == '' || term == null) {
                    this.resetSearch();
                }
            }),
        );

        // Close search when route changes
        this.subscription.add(
            this.router.events.subscribe(event => {
                if (event instanceof NavigationStart) {
                    this.isSearchOpen = false;
                }
            }),
        );
    }

    toggleSearch() {
        this.isSearchOpen = !this.isSearchOpen;
        // Focus element when opening the search
        if (this.isSearchOpen) {
            // Wait for the input to be visible/rendered before focusing
            setTimeout(() => {
                this.searchInput?.nativeElement.focus();
            });
        }
    }

    closeSearch() {
        this.isSearchOpen = false;
    }

    onFocusOut(event: FocusEvent) {
        const formEl = this.searchForm?.nativeElement;
        const nextTarget = event.relatedTarget as HTMLElement | null;

        // If the mobile version is open and focus moves to the mobile container (or nowhere),
        // ignore this focusout to avoid closing the overlay while interacting with it.
        const mobileEl = this.mobileContainer?.nativeElement ?? null;
        const isMobileOpen = !!(this.isSearchOpen && mobileEl && mobileEl.classList.contains('open'));
        if (isMobileOpen) {
            if (!nextTarget || (mobileEl && (nextTarget === mobileEl || mobileEl.contains(nextTarget)))) {
                return;
            }
        }

        // If we don't have the form element or focus moves outside the form (or to nowhere), close the search
        if (!formEl || !nextTarget || !formEl.contains(nextTarget)) {
            this.closeSearch();
        }
    }

    resetSearch() {
        this.search.reset();
        this.queryField.reset();
        this.showClearButton = false;
    }

    onSearch() {
        const term = this.queryField.value;
        if (term && term.trim().length > 0) {
            this.search.setQuery(term);
            this.router.navigate(['/search']);
            this.isSearchOpen = false;
            this.queryField.reset(null, { emitEvent: false });
            this.searchInput?.nativeElement.blur();
            this.searchSubmitted.emit();
        }
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
}
